import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { StepsForm } from '~/components';
import {
  AddressPage,
  ContactPage,
  IntroductionPage,
  PersonalInfoPage,
  ResumePage,
} from './components';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useAppNavigation from '~/hooks/useAppNavigation';
import useAlertReload from '~/hooks/useAlertReload';
import useBagProtectionForm from '~/stores/bagProtectionForm';
import { useSalesFunnel } from '~/stores';
import type { StepContent } from '~/components/StepsForm';
import { BagProtectionStepNames } from '~/utils/salesFunnel';

const BagProtectionContract: React.FC = () => {
  const { navigate, push } = useAppNavigation();
  const { form } = useBagProtectionForm();
  const { newStepLog } = useSalesFunnel();

  useAlertReload();

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_BagProtectionContract'>
    >();

  const currentStep = useMemo(() => {
    return Number(params?.step || 1);
  }, [params?.step]);

  const goToStep = useCallback(
    (step: number) => {
      push('MarisaServicos_BagProtectionContract', {
        step,
      });
    },
    [push]
  );

  const goToNextStep = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const onFinish = useCallback(() => {
    navigate('MarisaServicos_Checkout', { type: 'bag-protection' });
  }, [navigate]);

  const stepsFormData = useMemo<StepContent[]>(
    () => [
      {
        step: 1,
        content: <IntroductionPage onContinue={goToNextStep} />,
        stepName: BagProtectionStepNames.INTRODUCTION,
      },
      {
        step: 2,
        content: <PersonalInfoPage onContinue={goToNextStep} />,
        stepName: BagProtectionStepNames.PERSONAL_INFO,
      },
      {
        step: 3,
        content: <ContactPage onContinue={goToNextStep} />,
        stepName: BagProtectionStepNames.CONTACT,
      },
      {
        step: 4,
        content: <AddressPage onContinue={goToNextStep} />,
        stepName: BagProtectionStepNames.ADDRESS,
      },
      {
        step: 5,
        content: <ResumePage onContinue={onFinish} />,
        stepName: BagProtectionStepNames.RESUME,
      },
    ],
    [goToNextStep, onFinish]
  );

  const currentStepFormData = useMemo(
    () => stepsFormData.find((data) => data.step === currentStep),
    [currentStep, stepsFormData]
  );

  useEffect(() => {
    const { introduction, personalInfo, address, contact } = form;

    const steps: any[] = [introduction, personalInfo, contact, address];

    if (
      currentStep >= 2 &&
      Object.entries(steps[currentStep - 2]).length === 0
    ) {
      navigate('MarisaServicos_BagProtectionContract', {});
    }
  }, [currentStep, navigate, form]);

  useEffect(() => {
    if (currentStepFormData?.stepName) {
      newStepLog(currentStepFormData.stepName);
    }
  }, [currentStepFormData?.stepName, newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <StepsForm data={stepsFormData} currentStep={currentStep} />
    </View>
  );
};

export default BagProtectionContract;
